import { runWithLoading } from '../../redux/loadingOperation'
import firebase from 'firebase/compat/app'
import { forEach, intersection, isEqual, sortBy, uniq } from 'lodash'
import moment from 'moment'

import {
    getDb,
    mapGoalData,
    mapMilestoneData,
    mapTaskData,
    createGoalUpdatesChain,
    deleteGoalFeedsChain,
    updateGoalUpdatesChain,
    updateGoalProgressFeedsChain,
    updateGoalNameFeedsChain,
    updateGoalHighlightFeedsChain,
    updateGoalAssigneesCapacitiesFeedsChain,
    updateGoalAssigneesFeedsChain,
    updateGoalDescriptionFeedsChain,
    updateGoalProjectFeedsChain,
    createGenericTaskWhenMentionInTitleEdition,
    getId,
    generateSortIndex,
    globalWatcherUnsub,
    logEvent,
    getObjectFollowersIds,
    updateGoalPrivacyFeedsChain,
    generateNegativeSortIndex,
    getMentionedUsersIdsWhenEditText,
    runHttpsCallableFunction,
} from '../firestore'
import TasksHelper, {
    BACKLOG_DATE_NUMERIC,
    GENERIC_GOAL_TYPE,
    OPEN_STEP,
} from '../../../components/TaskListView/Utils/TasksHelper'
import store from '../../../redux/store'
import {
    getNewDefaultGoalMilestone,
    getPositiveDaysDifference,
    ONE_DAY_MILLISECONDS,
    BACKLOG_MILESTONE_ID,
    CAPACITY_NONE,
    DYNAMIC_PERCENT,
    ALL_USERS,
} from '../../../components/GoalsView/GoalsHelper'
import { DEFAULT_WORKSTREAM_ID } from '../../../components/Workstreams/WorkstreamHelper'
import ProjectHelper from '../../../components/SettingsView/ProjectsSettings/ProjectHelper'
import { FEED_PUBLIC_FOR_ALL } from '../../../components/Feeds/Utils/FeedsConstants'
import { getAllOwnerIds } from '../../../components/UIComponents/FloatModals/PrivacyModal/PrivacyModal'
import { updateXpByChangeGoalProgress } from '../../Levels'
import {
    setDoneMilestonesInProject,
    setGoalsInProject,
    setOpenMilestonesInProject,
    setSelectedNavItem,
    setSelectedSidebarTab,
    setSelectedTypeOfProject,
    switchProject,
} from '../../../redux/actions'
import { BatchWrapper } from '../../../functions/BatchWrapper/batchWrapper'
import { createFirstSnapshotPerformance } from '../../performance/firestoreSnapshotPerformance'
import { createCachedSnapshotGate } from '../cachedSnapshotGate'

import { createGoalAssistantChangedFeed } from './goalUpdates'
import { createGenericTaskWhenMention, setTaskProjectWithGoal } from '../Tasks/tasksFirestore'
import { updateNotePrivacy, updateNoteTitleWithoutFeed } from '../Notes/notesFirestore'
import {
    updateChatAssistantWithoutFeeds,
    updateChatPrivacy,
    updateChatTitleWithoutFeeds,
} from '../Chats/chatsFirestore'
import NavigationService from '../../NavigationService'
import { DV_TAB_GOAL_PROPERTIES, DV_TAB_ROOT_GOALS } from '../../TabNavigationConstants'
import {
    GOAL_MILESTONES_MODE_LINEAR,
    GOAL_SCHEDULE_MODE_DYNAMIC,
    GOAL_SCHEDULE_MODE_FIXED,
    MILESTONE_TYPE_FIXED,
    MILESTONE_TYPE_LINEAR,
    getLinearMilestonePeriods,
    getLinearMilestoneTitle,
    getGoalMilestoneTransition,
    normalizeGoalMilestonesConfig,
    normalizeGoalScheduleMode,
    normalizeMilestoneType,
} from '../../GoalMilestonesHelper'
import { handleOptionalSnapshotError } from '../optionalSnapshotError'
import { withoutServerAccessProjection } from '../accessProjection'
import { resolveFocusAreaForProjectMove } from './goalFocusAreas'

//ACCESS FUNCTIONS

export async function getGoalData(projectId, goalId) {
    const goal = (await getDb().doc(`/goals/${projectId}/items/${goalId}`).get()).data()
    return goal ? mapGoalData(goalId, goal) : null
}

export function watchGoal(projectId, goalId, watcherKey, callback) {
    globalWatcherUnsub[watcherKey] = getDb()
        .doc(`goals/${projectId}/items/${goalId}`)
        .onSnapshot(goalDoc => {
            const goalData = goalDoc.data()
            const goal = goalData ? mapGoalData(goalId, goalData) : null
            callback(goal)
        })
}

export function watchGoalsInDateRange(projectId, date1, date2, watcherKey, callback, ownerId) {
    const { uid: loggedUserId, isAnonymous } = store.getState().loggedUser
    const allowUserIds = isAnonymous ? [FEED_PUBLIC_FOR_ALL] : [FEED_PUBLIC_FOR_ALL, loggedUserId]

    globalWatcherUnsub[watcherKey] = getDb()
        .collection(`goals/${projectId}/items`)
        .where('completionMilestoneDate', '>=', date1)
        .where('readerIds', 'array-contains', allowUserIds[allowUserIds.length - 1])
        .where('ownerId', '==', ownerId)
        .onSnapshot(
            goalsDocs => {
                const goals = []
                goalsDocs.forEach(doc => {
                    const goal = mapGoalData(doc.id, doc.data())
                    if (goal.startingMilestoneDate <= date2) goals.push(goal)
                })
                callback(goals)
            },
            error => handleOptionalSnapshotError('goals in date range', error, () => callback([]))
        )
}

export function watchProjectOKRProgressByRange(projectId, timestamp1, timestamp2, watcherKey, callback) {
    const { uid: loggedUserId, isAnonymous } = store.getState().loggedUser
    const allowUserIds = isAnonymous ? [FEED_PUBLIC_FOR_ALL] : [FEED_PUBLIC_FOR_ALL, loggedUserId]

    globalWatcherUnsub[watcherKey] = getDb()
        .collection(`goals/${projectId}/items`)
        .where('readerIds', 'array-contains', allowUserIds[allowUserIds.length - 1])
        .onSnapshot(goalsDocs => {
            const entries = []

            goalsDocs.forEach(doc => {
                const goal = mapGoalData(doc.id, doc.data())

                Object.values(goal.progressByDoneMilestone).forEach(snapshot => {
                    if (
                        !snapshot ||
                        !Number.isFinite(snapshot.progress) ||
                        !snapshot.doneDate ||
                        snapshot.doneDate < timestamp1 ||
                        snapshot.doneDate > timestamp2
                    ) {
                        return
                    }

                    entries.push({
                        goalId: goal.id,
                        progress: snapshot.progress,
                        timestamp: snapshot.doneDate,
                    })
                })
            })

            callback(projectId, entries)
        })
}

export function watchAllGoals(
    projectId,
    watcherKey,
    ownerId,
    { manageLoading = true, trackConnectionHealth = true, onInitialSnapshot } = {}
) {
    const { uid: loggedUserId, isAnonymous } = store.getState().loggedUser
    const allowUserIds = isAnonymous ? [FEED_PUBLIC_FOR_ALL] : [FEED_PUBLIC_FOR_ALL, loggedUserId]
    const query = getDb()
        .collection(`goals/${projectId}/items`)
        .where('readerIds', 'array-contains', allowUserIds[allowUserIds.length - 1])
        .where('ownerId', '==', ownerId)
    const snapshotPerformance = createFirstSnapshotPerformance(
        { object_type: 'goals', scope: 'project', source: 'goals_board' },
        { sampleRate: 0.02 }
    )
    let initialSnapshotDelivered = false

    const finishInitialSnapshot = () => {
        if (initialSnapshotDelivered) return
        initialSnapshotDelivered = true
        onInitialSnapshot?.(projectId)
    }
    const gate = createCachedSnapshotGate(() => handleSnapshot, {
        trackConnectionHealth,
        connectionSource: 'goals_snapshot',
        loadingSource: manageLoading ? 'goals' : undefined,
        onError: err => {
            snapshotPerformance.fail()
            console.error('watchAllGoals: onSnapshot error', { projectId, ownerId, watcherKey, err })
            finishInitialSnapshot()
        },
    })

    function handleSnapshot(goalsData) {
        const buffered = gate.shouldBuffer(goalsData)
        snapshotPerformance.observe(goalsData, buffered)
        if (buffered) return

        try {
            const goals = []
            goalsData.forEach(doc => {
                const goal = mapGoalData(doc.id, doc.data())
                goals.push(goal)
            })
            store.dispatch(setGoalsInProject(projectId, goals))
            finishInitialSnapshot()
        } catch (err) {
            snapshotPerformance.fail()
            console.error('watchAllGoals: snapshot handler error', { projectId, ownerId, watcherKey, err })
            finishInitialSnapshot()
        }
    }

    const unsubscribe = gate.subscribe(query)
    globalWatcherUnsub[watcherKey] = unsubscribe
}

async function getGoalsInDoneMilestone(projectId, milestoneId, idsOfGoalsToExclude) {
    const goalsDocs = (
        await getDb()
            .collection(`goals/${projectId}/items`)
            .where('parentDoneMilestoneIds', 'array-contains-any', [milestoneId])
            .get()
    ).docs
    const goals = []
    goalsDocs.forEach(doc => {
        const goal = mapGoalData(doc.id, doc.data())
        if (!idsOfGoalsToExclude.includes(goal.id)) {
            goals.push(goal)
        }
    })
    return goals
}

async function getGoalsInOpenMilestone(
    projectId,
    milestoneDate,
    idsOfGoalsToExclude,
    getOnlyIncompleteGoalsInBacklog,
    ownerId,
    scheduleMode = null
) {
    const goalsDocs = (
        await getDb()
            .collection(`goals/${projectId}/items`)
            .where('completionMilestoneDate', '>=', milestoneDate)
            .where('ownerId', '==', ownerId)
            .get()
    ).docs
    const goals = []
    goalsDocs.forEach(doc => {
        const goal = mapGoalData(doc.id, doc.data())
        if (
            goal.startingMilestoneDate <= milestoneDate &&
            !idsOfGoalsToExclude.includes(goal.id) &&
            (milestoneDate !== BACKLOG_DATE_NUMERIC || !getOnlyIncompleteGoalsInBacklog || goal.progress !== 100) &&
            (!scheduleMode || normalizeGoalScheduleMode(goal.scheduleMode) === scheduleMode)
        ) {
            goals.push(goal)
        }
    })
    return goals
}

async function getBaseGoalsInOpenMilestone(
    projectId,
    milestoneDate,
    idsOfGoalsToExclude,
    ownerId,
    scheduleMode = null
) {
    const goalsDocs = (
        await getDb()
            .collection(`goals/${projectId}/items`)
            .where('completionMilestoneDate', '==', milestoneDate)
            .where('ownerId', '==', ownerId)
            .get()
    ).docs
    const goals = []
    goalsDocs.forEach(doc => {
        const goal = mapGoalData(doc.id, doc.data())
        if (!idsOfGoalsToExclude.includes(goal.id) && (!scheduleMode || goal.scheduleMode === scheduleMode)) {
            goals.push(goal)
        }
    })
    return goals
}

export async function watchBaseGoalsAmountInOpenMilestone(projectId, milestoneDate, callback, watcherKey, ownerId) {
    globalWatcherUnsub[watcherKey] = getDb()
        .collection(`goals/${projectId}/items`)
        .where('completionMilestoneDate', '==', milestoneDate)
        .where('ownerId', '==', ownerId)
        .onSnapshot(
            goalsDocs => {
                callback(goalsDocs.docs.length)
            },
            error => handleOptionalSnapshotError('goals in milestone count', error, () => callback(0))
        )
}

export const getPreviousMilestone = async (projectId, ownerId, date) => {
    const milestonesDocs = (
        await getDb()
            .collection(`goalsMilestones/${projectId}/milestonesItems`)
            .where('date', '<', date)
            .where('done', '==', false)
            .where('ownerId', '==', ownerId)
            .orderBy('date', 'desc')
            .limit(1)
            .get()
    ).docs

    const milestone =
        milestonesDocs.length > 0 ? mapMilestoneData(milestonesDocs[0].id, milestonesDocs[0].data()) : null

    return milestone
}

async function getOpenMilestonesFromGoal(projectId, goal) {
    const { startingMilestoneDate, completionMilestoneDate, ownerId } = goal
    const milestones = await getOpenMilestonesInDateRange(
        projectId,
        startingMilestoneDate,
        completionMilestoneDate,
        ownerId,
        goal.scheduleMode === GOAL_SCHEDULE_MODE_DYNAMIC ? MILESTONE_TYPE_LINEAR : MILESTONE_TYPE_FIXED
    )
    return milestones
}

export function watchAllMilestones(
    projectId,
    watcherKey,
    ownerId,
    { manageLoading = true, trackConnectionHealth = true, onInitialSnapshot } = {}
) {
    const snapshotPerformance = createFirstSnapshotPerformance(
        { object_type: 'milestones', scope: 'project', source: 'goals_board' },
        { sampleRate: 0.02 }
    )
    let initialSnapshotDelivered = false

    const finishInitialSnapshot = () => {
        if (initialSnapshotDelivered) return
        initialSnapshotDelivered = true
        onInitialSnapshot?.(projectId)
    }
    const gate = createCachedSnapshotGate(() => handleSnapshot, {
        trackConnectionHealth,
        connectionSource: 'milestones_snapshot',
        loadingSource: manageLoading ? 'milestones' : undefined,
        onError: err => {
            snapshotPerformance.fail()
            console.error('watchAllMilestones: onSnapshot error', { projectId, ownerId, watcherKey, err })
            finishInitialSnapshot()
        },
    })
    const query = getDb()
        .collection(`goalsMilestones/${projectId}/milestonesItems`)
        .where('ownerId', '==', ownerId)
        .orderBy('date', 'asc')

    function handleSnapshot(milestonesData) {
        const buffered = gate.shouldBuffer(milestonesData)
        snapshotPerformance.observe(milestonesData, buffered)
        if (buffered) return

        try {
            const openMilestones = []
            let doneMilestone = []
            milestonesData.forEach(doc => {
                const milestone = mapMilestoneData(doc.id, doc.data())
                milestone.done ? doneMilestone.push(milestone) : openMilestones.push(milestone)
            })

            doneMilestone = sortBy(doneMilestone, [item => item.doneDate])
            doneMilestone.reverse()

            store.dispatch([
                setOpenMilestonesInProject(projectId, openMilestones),
                setDoneMilestonesInProject(projectId, doneMilestone),
            ])
            finishInitialSnapshot()
        } catch (err) {
            snapshotPerformance.fail()
            console.error('watchAllMilestones: snapshot handler error', { projectId, ownerId, watcherKey, err })
            finishInitialSnapshot()
        }
    }

    const unsubscribe = gate.subscribe(query)
    globalWatcherUnsub[watcherKey] = unsubscribe
}

export function watchMilestones(projectId, callback, milestonesInDone, watcherKey, ownerId) {
    let query = getDb()
        .collection(`goalsMilestones/${projectId}/milestonesItems`)
        .where('done', '==', milestonesInDone)
        .where('ownerId', '==', ownerId)
    query = milestonesInDone ? query.orderBy('doneDate', 'desc') : query.orderBy('date', 'asc')

    globalWatcherUnsub[watcherKey] = query.onSnapshot(milestonesData => {
        const milestones = []
        milestonesData.forEach(doc => {
            const milestone = mapMilestoneData(doc.id, doc.data())
            milestones.push(milestone)
        })
        callback(projectId, milestones)
    })
}

export function watchMilestoneTasksStatistics(
    projectId,
    milestoneInitalDate,
    milestoneEndDate,
    inDone,
    watcherKey,
    callback
) {
    const loggedUserId = store.getState().loggedUser.uid

    globalWatcherUnsub[watcherKey] = getDb()
        .collection(`items/${projectId}/tasks`)
        .where('readerIds', 'array-contains', loggedUserId)
        .where('dueDate', '>', milestoneInitalDate)
        .where('dueDate', '<=', milestoneEndDate)
        .where('done', '==', inDone)
        .where('parentId', '==', null)
        .onSnapshot(
            tasksData => {
                let amountOfTasks = 0
                let amountOfPoints = 0
                tasksData.forEach(doc => {
                    const task = mapTaskData(doc.id, doc.data())
                    const { isPublicFor, estimations } = task
                    const loggedUserHaveAccess =
                        !isPublicFor ||
                        isPublicFor.length === 0 ||
                        isPublicFor.includes(FEED_PUBLIC_FOR_ALL) ||
                        isPublicFor.includes(loggedUserId)
                    if (loggedUserHaveAccess) {
                        amountOfTasks++
                        if (estimations && estimations[OPEN_STEP]) {
                            amountOfPoints += estimations[OPEN_STEP]
                        }
                    }
                })
                callback(amountOfTasks, amountOfPoints)
            },
            error => handleOptionalSnapshotError('milestone task statistics', error, () => callback(0, 0))
        )
}

export async function getMilestoneData(projectId, milestoneId) {
    const milestone = (await getDb().doc(`/goalsMilestones/${projectId}/milestonesItems/${milestoneId}`).get()).data()
    return milestone ? mapMilestoneData(milestoneId, milestone) : null
}

async function getOpenMilestonesInDateRange(projectId, date1, date2, ownerId, milestoneType = null) {
    const milestonesDocs = (
        await getDb()
            .collection(`goalsMilestones/${projectId}/milestonesItems`)
            .where('date', '>=', date1)
            .where('date', '<=', date2)
            .where('done', '==', false)
            .where('ownerId', '==', ownerId)
            .orderBy('date', 'asc')
            .get()
    ).docs

    const milestones = []
    const normalizedType = milestoneType ? normalizeMilestoneType(milestoneType) : null
    milestonesDocs.forEach(doc => {
        if (!normalizedType || normalizeMilestoneType(doc.data().milestoneType) === normalizedType) {
            milestones.push(mapMilestoneData(doc.id, doc.data()))
        }
    })
    return milestones
}

export async function getNextMilestones(projectId, ownerId, amountOfNextMilestones) {
    const milestoneDocs = await getDb()
        .collection(`goalsMilestones/${projectId}/milestonesItems`)
        .where('done', '==', false)
        .where('ownerId', '==', ownerId)
        .orderBy('date', 'asc')
        .limit(amountOfNextMilestones)
        .get()

    const milestones = []
    milestoneDocs.forEach(doc => {
        milestones.push(mapMilestoneData(doc.id, doc.data()))
    })
    return milestones
}

export async function getActiveMilestone(projectId, ownerId) {
    const milestones = await getNextMilestones(projectId, ownerId, 1)
    return milestones[0]
}

export function watchActiveMilestone(projectId, watcherKey, callback, ownerId) {
    globalWatcherUnsub[watcherKey] = getDb()
        .collection(`goalsMilestones/${projectId}/milestonesItems`)
        .where('done', '==', false)
        .where('ownerId', '==', ownerId)
        .orderBy('date', 'asc')
        .limit(1)
        .onSnapshot(milestoneDocs => {
            const milestoneDoc = milestoneDocs.docs[0]
            const milestone = milestoneDoc ? mapMilestoneData(milestoneDoc.id, milestoneDoc.data()) : null
            callback(milestone)
        })
}

export async function getMilestoneUsingDate(projectId, date, searchInDoneMilestones, ownerId, milestoneType = null) {
    const milestoneDocs = (
        await getDb()
            .collection(`goalsMilestones/${projectId}/milestonesItems`)
            .where('date', '==', date)
            .where('done', '==', searchInDoneMilestones)
            .where('ownerId', '==', ownerId)
            .get()
    ).docs
    const normalizedType = milestoneType ? normalizeMilestoneType(milestoneType) : null
    const milestoneDoc = normalizedType
        ? milestoneDocs.find(doc => normalizeMilestoneType(doc.data().milestoneType) === normalizedType)
        : milestoneDocs[0]
    return milestoneDoc ? mapMilestoneData(milestoneDoc.id, milestoneDoc.data()) : null
}

async function getLinearMilestoneUsingPeriodKey(projectId, periodKey, done, ownerId) {
    const milestoneDoc = (
        await getDb()
            .collection(`goalsMilestones/${projectId}/milestonesItems`)
            .where('periodKey', '==', periodKey)
            .where('done', '==', done)
            .where('ownerId', '==', ownerId)
            .limit(1)
            .get()
    ).docs[0]
    return milestoneDoc ? mapMilestoneData(milestoneDoc.id, milestoneDoc.data()) : null
}

function buildLinearMilestone(period, ownerId, done = false) {
    return {
        ...getNewDefaultGoalMilestone(),
        extendedName: getLinearMilestoneTitle(period),
        date: period.date,
        done,
        ownerId,
        milestoneType: MILESTONE_TYPE_LINEAR,
        periodStartDate: period.periodStartDate,
        periodEndDate: period.periodEndDate,
        periodKey: period.periodKey,
        cadence: period.cadence,
    }
}

export async function ensureLinearMilestonesForProject(projectId, ownerId, config, startTimestamp = Date.now()) {
    let normalizedConfig = normalizeGoalMilestonesConfig(config)
    if (normalizedConfig.mode !== GOAL_MILESTONES_MODE_LINEAR) {
        const projectRef = getDb().doc(`/projects/${projectId}`)
        const projectDoc = await projectRef.get()
        normalizedConfig = normalizeGoalMilestonesConfig(projectDoc.data()?.goalMilestonesConfig || config)

        if (normalizedConfig.mode !== GOAL_MILESTONES_MODE_LINEAR) {
            normalizedConfig = { ...normalizedConfig, mode: GOAL_MILESTONES_MODE_LINEAR }
            await projectRef.update({ goalMilestonesConfig: normalizedConfig })
        }
    }

    const periods = getLinearMilestonePeriods(
        normalizedConfig,
        startTimestamp,
        normalizedConfig.futureMilestonesToCreate + 1
    )
    const milestones = []

    for (let i = 0; i < periods.length; i++) {
        const period = periods[i]
        let milestone = await getLinearMilestoneUsingPeriodKey(projectId, period.periodKey, false, ownerId)
        if (!milestone) {
            milestone = buildLinearMilestone(period, ownerId, false)
            const milestoneId = getId()
            await getDb().collection(`goalsMilestones/${projectId}/milestonesItems`).doc(milestoneId).set(milestone)
            milestone.id = milestoneId
            await addOpenMilestoneSortIndexToGoals(
                projectId,
                milestoneId,
                milestone.date,
                null,
                ownerId,
                GOAL_SCHEDULE_MODE_DYNAMIC
            )
        }
        milestones.push(milestone)
    }

    return milestones
}

async function ensureLinearMilestonesIncludingDate(projectId, ownerId, config, milestoneDate) {
    const currentMilestones = await ensureLinearMilestonesForProject(projectId, ownerId, config)
    if (
        milestoneDate === BACKLOG_DATE_NUMERIC ||
        currentMilestones.some(milestone => milestone.date === milestoneDate)
    ) {
        return currentMilestones
    }

    const selectedMilestones = await ensureLinearMilestonesForProject(projectId, ownerId, config, milestoneDate)
    return [...currentMilestones, ...selectedMilestones]
}

async function getActiveLinearMilestone(projectId, ownerId, config) {
    const milestones = await ensureLinearMilestonesForProject(projectId, ownerId, config)
    return milestones[0] || null
}

export function watchOpenMilestonesInDateRange(projectId, date1, date2, watcherKey, callback, ownerId) {
    globalWatcherUnsub[watcherKey] = getDb()
        .collection(`goalsMilestones/${projectId}/milestonesItems`)
        .where('date', '>=', date1)
        .where('date', '<=', date2)
        .where('done', '==', false)
        .where('ownerId', '==', ownerId)
        .orderBy('date', 'asc')
        .onSnapshot(
            milestoneDocs => {
                const milestones = []
                milestoneDocs.forEach(doc => {
                    const milestone = mapMilestoneData(doc.id, doc.data())
                    milestones.push(milestone)
                })
                callback(milestones)
            },
            error => handleOptionalSnapshotError('open milestones in date range', error, () => callback([]))
        )
}

//EDTION AND ADITION FUNCTIONS

export const updateGoalEditionData = async (projectId, goalId, editorId) => {
    await getDb().runTransaction(async transaction => {
        const ref = getDb().doc(`goals/${projectId}/items/${goalId}`)
        const doc = await transaction.get(ref)
        if (doc.exists) transaction.update(ref, { lastEditionDate: Date.now(), lastEditorId: editorId })
    })
}

const updateEditionData = data => {
    const { loggedUser } = store.getState()
    data.lastEditionDate = Date.now()
    data.lastEditorId = loggedUser.uid
}

async function updateGoalData(projectId, goalId, data, batch) {
    updateEditionData(data)
    const ref = getDb().doc(`goals/${projectId}/items/${goalId}`)
    batch ? batch.update(ref, data) : await ref.update(data)
}

export async function uploadNewGoal(
    projectId,
    goal,
    baseDate,
    movingGoalToOtherProject,
    milestoneType = MILESTONE_TYPE_FIXED
) {
    const { loggedUser } = store.getState()

    updateEditionData(goal)

    goal.extendedName = goal.extendedName.trim()
    const cleanedTitle = TasksHelper.getTaskNameWithoutMeta(goal.extendedName)

    goal.name = cleanedTitle.toLowerCase()
    goal.created = Date.now()

    const project = ProjectHelper.getProjectById(projectId)
    const ownerId = project.parentTemplateId ? goal.assigneesIds[0] : ALL_USERS
    goal.ownerId = ownerId
    goal.scheduleMode =
        normalizeMilestoneType(milestoneType) === MILESTONE_TYPE_LINEAR
            ? GOAL_SCHEDULE_MODE_DYNAMIC
            : normalizeGoalScheduleMode(goal.scheduleMode)

    if (!goal.id) goal.id = getId()

    const fullText = goal.extendedName + ' ' + goal.description
    const mentionedUserIds = intersection(project.userIds, getMentionedUsersIdsWhenEditText(fullText, ''))

    createGenericTaskWhenMention(projectId, goal.id, mentionedUserIds, GENERIC_GOAL_TYPE, 'goals', goal.assistantId)

    if (baseDate && goal.startingMilestoneDate !== goal.completionMilestoneDate) {
        baseDate === goal.completionMilestoneDate
            ? (goal.completionMilestoneDate = goal.startingMilestoneDate)
            : (goal.startingMilestoneDate = goal.completionMilestoneDate)
    }

    if (goal.scheduleMode === GOAL_SCHEDULE_MODE_DYNAMIC) {
        await ensureLinearMilestonesIncludingDate(
            projectId,
            ownerId,
            project.goalMilestonesConfig,
            goal.completionMilestoneDate
        )
    } else {
        uploadOpenNewMilestoneIfNotExistMilestoneInSameDate(projectId, goal.completionMilestoneDate, ownerId)
    }

    goal.assigneesReminderDate = await generateAssigneesReminderDate(
        movingGoalToOtherProject ? goal.assigneesReminderDate : {},
        goal.assigneesIds,
        projectId,
        goal.ownerId,
        goal.completionMilestoneDate
    )

    const goalToStore = withoutServerAccessProjection(goal)
    delete goalToStore.id
    getDb().doc(`goals/${projectId}/items/${goal.id}`).set(goalToStore, { merge: true })

    const useNegativeSortIndex = loggedUser.templateProjectIds.includes(projectId)
    updateAllOpenGoalSortIndexs(projectId, goal, useNegativeSortIndex)

    createGoalUpdatesChain(projectId, goal)

    logEvent('new_goal', {
        id: goal.id,
        name: goal.name,
    })

    return goal
}

export async function deleteAllGoalsInMilestone(projectId, goals) {
    goals.forEach(goal => {
        deleteGoal(projectId, goal, '')
    })
}

export async function deleteGoal(projectId, goal, movingToOtherProjectId) {
    if (movingToOtherProjectId)
        await getDb().doc(`goals/${projectId}/items/${goal.id}`).update({ movingToOtherProjectId })
    await getDb().doc(`goals/${projectId}/items/${goal.id}`).delete()
    deleteGoalFeedsChain(projectId, goal)
}

export async function updateGoal(projectId, oldGoal, updatedGoal, avoidFollow) {
    const { loggedUser } = store.getState()

    if (oldGoal.extendedName !== updatedGoal.extendedName) {
        updatedGoal.extendedName = updatedGoal.extendedName.trim()
        const cleanedTitle = TasksHelper.getTaskNameWithoutMeta(updatedGoal.extendedName)
        updatedGoal.name = cleanedTitle.toLowerCase()

        createGenericTaskWhenMentionInTitleEdition(
            projectId,
            updatedGoal.id,
            updatedGoal.extendedName,
            oldGoal.extendedName,
            GENERIC_GOAL_TYPE,
            'goals',
            updatedGoal.assistantId
        )
    }

    if (oldGoal.description !== updatedGoal.description) {
        createGenericTaskWhenMentionInTitleEdition(
            projectId,
            updatedGoal.id,
            updatedGoal.description,
            oldGoal.description,
            GENERIC_GOAL_TYPE,
            'goals',
            updatedGoal.assistantId
        )
    }

    if (oldGoal.progress !== updatedGoal.progress) {
        updateXpByChangeGoalProgress(loggedUser.uid, firebase, getDb(), projectId)
    }

    let newIsPublicFor = updatedGoal.isPublicFor
    if (!isEqual(oldGoal.assigneesIds, updatedGoal.assigneesIds)) {
        newIsPublicFor = getNewIsPublicForWhenAssigneesChanges(
            projectId,
            updatedGoal.isPublicFor,
            updatedGoal.assigneesIds
        )
    }

    const promisesToProcess = []
    const scheduleModeChanged =
        normalizeGoalScheduleMode(oldGoal.scheduleMode) !== normalizeGoalScheduleMode(updatedGoal.scheduleMode)
    const milestoneDatesChanged =
        oldGoal.startingMilestoneDate !== updatedGoal.startingMilestoneDate ||
        oldGoal.completionMilestoneDate !== updatedGoal.completionMilestoneDate

    if (scheduleModeChanged && !milestoneDatesChanged && updatedGoal.completionMilestoneDate !== BACKLOG_DATE_NUMERIC) {
        if (normalizeGoalScheduleMode(updatedGoal.scheduleMode) === GOAL_SCHEDULE_MODE_DYNAMIC) {
            const project = ProjectHelper.getProjectById(projectId)
            await ensureLinearMilestonesIncludingDate(
                projectId,
                updatedGoal.ownerId,
                project?.goalMilestonesConfig,
                updatedGoal.completionMilestoneDate
            )
            await deleteOpenMilestoneIfIsEmpty(
                projectId,
                updatedGoal.completionMilestoneDate,
                [updatedGoal.id],
                updatedGoal.ownerId
            )
        } else {
            await uploadOpenNewMilestoneIfNotExistMilestoneInSameDate(
                projectId,
                updatedGoal.completionMilestoneDate,
                updatedGoal.ownerId
            )
        }
        await updateAllOpenGoalSortIndexs(projectId, updatedGoal, false)
    }

    if (oldGoal.startingMilestoneDate !== updatedGoal.startingMilestoneDate) {
        const newDate = updatedGoal.startingMilestoneDate

        const { toBacklog, fromBacklog, moveFullGoalWhenUpdateStartingDate } = getGoalDirectionWhenChangesDate(
            newDate,
            oldGoal,
            'startingMilestoneDate'
        )

        if (toBacklog || fromBacklog || moveFullGoalWhenUpdateStartingDate) {
            updatedGoal.completionMilestoneDate = newDate
        }

        promisesToProcess.push(
            updateGoalDateRange(
                projectId,
                {
                    ...updatedGoal,
                    startingMilestoneDate: oldGoal.startingMilestoneDate,
                    completionMilestoneDate: oldGoal.completionMilestoneDate,
                },
                newDate,
                'startingMilestoneDate',
                false
            )
        )
    } else if (oldGoal.completionMilestoneDate !== updatedGoal.completionMilestoneDate) {
        const newDate = updatedGoal.completionMilestoneDate

        const { toBacklog, fromBacklog, moveFullGoalWhenUpdateCompletionDate } = getGoalDirectionWhenChangesDate(
            newDate,
            oldGoal,
            'completionMilestoneDate'
        )

        if (toBacklog || fromBacklog || moveFullGoalWhenUpdateCompletionDate) {
            updatedGoal.startingMilestoneDate = newDate
        }

        promisesToProcess.push(
            updateGoalDateRange(
                projectId,
                {
                    ...updatedGoal,
                    startingMilestoneDate: oldGoal.startingMilestoneDate,
                    completionMilestoneDate: oldGoal.completionMilestoneDate,
                },
                newDate,
                'completionMilestoneDate',
                false
            )
        )
    }

    if (oldGoal.completionMilestoneDate === updatedGoal.completionMilestoneDate) {
        updatedGoal.assigneesReminderDate = await generateAssigneesReminderDate(
            updatedGoal.assigneesReminderDate,
            updatedGoal.assigneesIds,
            projectId,
            updatedGoal.ownerId,
            updatedGoal.completionMilestoneDate
        )
    } else {
        updatedGoal.assigneesReminderDate = updateAssigneesReminderDate(
            updatedGoal.assigneesIds,
            updatedGoal.completionMilestoneDate
        )
    }

    if (!isEqual(oldGoal.isPublicFor, newIsPublicFor)) {
        updatePrivacyInChildrenTasks(projectId, updatedGoal.id, newIsPublicFor)
        updateChatPrivacy(projectId, updatedGoal.id, 'goals', newIsPublicFor)
        if (updatedGoal.noteId) {
            const followersIds = await getObjectFollowersIds(projectId, 'goals', updatedGoal.id)
            updateNotePrivacy(
                projectId,
                updatedGoal.noteId,
                !newIsPublicFor.includes(FEED_PUBLIC_FOR_ALL),
                newIsPublicFor,
                followersIds,
                false,
                null
            )
        }
    }

    const goalToStore = { ...updatedGoal, isPublicFor: newIsPublicFor }
    delete goalToStore.id

    promisesToProcess.push(updateGoalData(projectId, updatedGoal.id, goalToStore, null))

    await Promise.all(promisesToProcess)

    updateGoalUpdatesChain(projectId, oldGoal, updatedGoal, avoidFollow)
}

export async function updateGoalDateRange(
    projectId,
    goal,
    newDate,
    rangeEdgePropertyName,
    needToUpdateGoal,
    milestoneType
) {
    const scheduleModeUpdate = milestoneType
        ? {
              scheduleMode:
                  normalizeMilestoneType(milestoneType) === MILESTONE_TYPE_LINEAR
                      ? GOAL_SCHEDULE_MODE_DYNAMIC
                      : GOAL_SCHEDULE_MODE_FIXED,
          }
        : {}

    if (newDate === goal[rangeEdgePropertyName]) {
        if (
            scheduleModeUpdate.scheduleMode &&
            normalizeGoalScheduleMode(goal.scheduleMode) !== scheduleModeUpdate.scheduleMode
        ) {
            await updateGoalDates(projectId, goal, newDate, scheduleModeUpdate, needToUpdateGoal, true)
        }
        return
    }

    const { toBacklog, fromBacklog, moveFullGoalWhenUpdateCompletionDate, moveFullGoalWhenUpdateStartingDate } =
        getGoalDirectionWhenChangesDate(newDate, goal, rangeEdgePropertyName)

    if (toBacklog || fromBacklog || moveFullGoalWhenUpdateCompletionDate || moveFullGoalWhenUpdateStartingDate) {
        await updateGoalDates(
            projectId,
            goal,
            newDate,
            {
                startingMilestoneDate: newDate,
                completionMilestoneDate: newDate,
                ...scheduleModeUpdate,
            },
            needToUpdateGoal,
            true
        )
    } else if (rangeEdgePropertyName === 'startingMilestoneDate') {
        await updateGoalDates(
            projectId,
            goal,
            newDate,
            {
                startingMilestoneDate: newDate,
                ...scheduleModeUpdate,
            },
            needToUpdateGoal,
            false
        )
    } else {
        await updateGoalDates(
            projectId,
            goal,
            newDate,
            { completionMilestoneDate: newDate, ...scheduleModeUpdate },
            needToUpdateGoal,
            true
        )
    }
}

async function updateGoalDates(projectId, goal, newDate, datesData, needToUpdateGoal, needToHandleMilestoneExistence) {
    const promises = []
    const { updatedGoal, previousCompletionMilestoneDate } = getGoalMilestoneTransition(goal, datesData)
    if (needToUpdateGoal) {
        promises.push(
            updateGoalData(
                projectId,
                goal.id,
                {
                    ...datesData,
                    assigneesReminderDate:
                        datesData.completionMilestoneDate &&
                        datesData.completionMilestoneDate !== goal.completionMilestoneDate
                            ? updateAssigneesReminderDate(goal.assigneesIds, datesData.completionMilestoneDate)
                            : goal.assigneesReminderDate,
                },
                null
            )
        )
    }
    if (needToHandleMilestoneExistence)
        promises.push(
            handleMilestonesExistenceWhenAGoalDateRangeChanges(
                projectId,
                updatedGoal,
                newDate,
                previousCompletionMilestoneDate
            )
        )
    promises.push(updateAllOpenGoalSortIndexs(projectId, updatedGoal, false))
    await Promise.all(promises)
}

export async function updateGoalNote(projectId, goalId, noteId) {
    await updateGoalData(projectId, goalId, { noteId }, null)
}

export function updateGoalHighlight(projectId, hasStar, goal) {
    updateGoalData(projectId, goal.id, { hasStar }, null)
    updateGoalHighlightFeedsChain(projectId, hasStar, goal)
}

export async function updateGoalPrivacy(projectId, isPublicFor, goal) {
    const goalId = goal.id
    updatePrivacyInChildrenTasks(projectId, goal.id, isPublicFor)
    await updateGoalData(projectId, goal.id, { isPublicFor }, null)
    updateChatPrivacy(projectId, goalId, 'goals', isPublicFor)
    if (goal.noteId) {
        const followersIds = await getObjectFollowersIds(projectId, 'goals', goalId)
        updateNotePrivacy(
            projectId,
            goal.noteId,
            !isPublicFor.includes(FEED_PUBLIC_FOR_ALL),
            isPublicFor,
            followersIds,
            false,
            null
        )
    }
    await updateGoalPrivacyFeedsChain(projectId, isPublicFor, goalId, goal, null)
}

export function updateGoalName(projectId, goalId, oldName, newName, goal) {
    const cleanedTitle = TasksHelper.getTaskNameWithoutMeta(newName)

    updateGoalData(projectId, goalId, { name: cleanedTitle.toLowerCase(), extendedName: newName }, null)

    updateChatTitleWithoutFeeds(projectId, goal.id, newName)

    if (goal.noteId) updateNoteTitleWithoutFeed(projectId, goal.noteId, newName)

    createGenericTaskWhenMentionInTitleEdition(
        projectId,
        goalId,
        newName,
        oldName,
        GENERIC_GOAL_TYPE,
        'goals',
        goal.assistantId
    )

    updateGoalNameFeedsChain(projectId, goalId, oldName, newName, goal)
}

export const setGoalAssistant = async (projectId, goalId, assistantId, needGenerateUpdate) => {
    const batch = new BatchWrapper(getDb())
    updateGoalData(projectId, goalId, { assistantId }, batch)
    await updateChatAssistantWithoutFeeds(projectId, goalId, assistantId, batch)
    await batch.commit()
    if (needGenerateUpdate) await createGoalAssistantChangedFeed(projectId, assistantId, goalId, null, null)
}

export async function updateGoalAssigneesIds(
    projectId,
    goalId,
    oldAssigneesIds,
    newAssigneesIds,
    goal,
    oldAssigneesCapacity,
    newAssigneesCapacity
) {
    const assigneesReminderDate = await generateAssigneesReminderDate(
        goal.assigneesReminderDate,
        newAssigneesIds,
        projectId,
        goal.ownerId,
        goal.completionMilestoneDate
    )

    const newIsPublicFor = getNewIsPublicForWhenAssigneesChanges(projectId, goal.isPublicFor, newAssigneesIds)
    updateGoalPrivacy(projectId, newIsPublicFor, goal)

    updateGoalData(
        projectId,
        goalId,
        {
            assigneesIds: newAssigneesIds,
            assigneesCapacity: newAssigneesCapacity,
            assigneesReminderDate,
        },
        null
    )

    updateGoalAssigneesFeedsChain(
        projectId,
        oldAssigneesIds,
        newAssigneesIds,
        goal,
        oldAssigneesCapacity,
        newAssigneesCapacity
    )
}

export async function updateGoalAssigneeCapacity(projectId, goal, oldCapacity, newCapacity, assigneeId) {
    updateGoalData(projectId, goal.id, { [`assigneesCapacity.${assigneeId}`]: newCapacity }, null)
    updateGoalAssigneesCapacitiesFeedsChain(projectId, goal, oldCapacity, newCapacity, assigneeId)
}

export async function updateGoalScheduleMode(projectId, goal, nextMode) {
    const scheduleMode = normalizeGoalScheduleMode(nextMode)

    if (scheduleMode === GOAL_SCHEDULE_MODE_FIXED) {
        await updateGoalData(projectId, goal.id, { scheduleMode }, null)
        if (goal.completionMilestoneDate !== BACKLOG_DATE_NUMERIC) {
            await uploadOpenNewMilestoneIfNotExistMilestoneInSameDate(
                projectId,
                goal.completionMilestoneDate,
                goal.ownerId
            )
            const fixedMilestone = await getMilestoneUsingDate(
                projectId,
                goal.completionMilestoneDate,
                false,
                goal.ownerId,
                MILESTONE_TYPE_FIXED
            )
            if (fixedMilestone) await updateGoalSortIndexes(projectId, goal.id, fixedMilestone.id)
        }
        return
    }

    const project = ProjectHelper.getProjectById(projectId)
    if (!project) return

    const activeLinearMilestone = await getActiveLinearMilestone(projectId, goal.ownerId, project.goalMilestonesConfig)
    if (!activeLinearMilestone) return

    const updateData = {
        scheduleMode,
        startingMilestoneDate: activeLinearMilestone.date,
        completionMilestoneDate: activeLinearMilestone.date,
        assigneesReminderDate: updateAssigneesReminderDate(goal.assigneesIds, activeLinearMilestone.date),
    }

    await updateGoalData(projectId, goal.id, updateData, null)
    await deleteOpenMilestoneIfIsEmpty(projectId, goal.completionMilestoneDate, [goal.id], goal.ownerId)
    await updateAllOpenGoalSortIndexs(projectId, { ...goal, ...updateData }, false)
}

export async function updateGoalAssigneeReminderDate(
    projectId,
    goalId,
    userId,
    date,
    goal = null,
    cascadeToTasks = false
) {
    const goalData = goal || (await getGoalData(projectId, goalId))
    const oldDate = goalData?.assigneesReminderDate?.[userId] || 0

    const updates = { [`assigneesReminderDate.${userId}`]: date }

    // Increment timesPostponed if date is being moved later (postponed)
    if (date > oldDate && date !== BACKLOG_DATE_NUMERIC) {
        updates.timesPostponed = firebase.firestore.FieldValue.increment(1)
        logEvent('goal_postponed')
    }

    const goalWrite = updateGoalData(projectId, goalId, updates, null)

    // Cascade to child tasks if requested
    if (cascadeToTasks) {
        await Promise.all([goalWrite, updateChildTasksDueDate(projectId, goalId, date)])
    } else {
        await goalWrite
    }
}

export const getDateToMoveGoalInAutoPostpone = timesPostponed => {
    let date = moment()

    if (!timesPostponed) {
        date.add(3, 'days')
    } else if (timesPostponed === 1) {
        date.add(1, 'week')
    } else if (timesPostponed === 2) {
        date.add(1, 'month')
    } else if (timesPostponed === 3) {
        date.add(3, 'months')
    } else if (timesPostponed === 4) {
        date.add(6, 'months')
    } else if (timesPostponed === 5) {
        date.add(1, 'year')
    } else {
        date = BACKLOG_DATE_NUMERIC
    }
    return date
}

const updateChildTasksDueDate = async (projectId, goalId, newDate) => {
    const endOfToday = moment().endOf('day').valueOf()

    const docs = await getDb()
        .collection(`/items/${projectId}/tasks`)
        .where('parentGoalId', '==', goalId)
        .where('done', '==', false)
        .get()

    if (docs.empty) return

    const batch = new BatchWrapper(getDb())

    for (const doc of docs.docs) {
        const task = doc.data()
        // Only update tasks whose dueDate is today or earlier
        if (task.dueDate <= endOfToday) {
            const taskUpdates = {
                dueDate: newDate,
                sortIndex: generateSortIndex(),
                timesPostponed: firebase.firestore.FieldValue.increment(1),
            }
            batch.update(getDb().doc(`items/${projectId}/tasks/${doc.id}`), taskUpdates)
        }
    }

    await batch.commit()
}

export async function autoPostponeGoal(projectId, goal, userId, cascadeToTasks = true, { background = false } = {}) {
    return runWithLoading(
        'postpone_goal',
        async () => {
            const date = getDateToMoveGoalInAutoPostpone(goal.timesPostponed)
            const dateTimestamp = date === BACKLOG_DATE_NUMERIC ? BACKLOG_DATE_NUMERIC : date.valueOf()
            // Routed through the offline-aware funnel (AT-2340): offline this now
            // fails immediately with `code: 'offline'` instead of hanging for the
            // SDK's ~70s timeout with the loading indicator up. The postpone is
            // server-side (it writes the undo action too), so there is nothing
            // useful to do locally — failing fast is the correct behaviour.
            const result = await runHttpsCallableFunction('postponeGoalWithUndoSecondGen', {
                projectId,
                goalId: goal.id,
                targetUserId: userId,
                date: dateTimestamp,
                endOfToday: moment().endOf('day').valueOf(),
                cascadeToTasks,
                requestId: getId(),
            })
            logEvent('goal_postponed')
            return result?.date ?? dateTimestamp
        },
        { enabled: !background }
    )
}

export const updateGoalLastCommentData = async (projectId, goalId, lastComment, lastCommentType) => {
    getDb()
        .doc(`goals/${projectId}/items/${goalId}`)
        .update({
            [`commentsData.lastComment`]: lastComment,
            [`commentsData.lastCommentType`]: lastCommentType,
            [`commentsData.amount`]: firebase.firestore.FieldValue.increment(1),
        })
}

export const resetGoalLastCommentData = async (projectId, goalId) => {
    const ref = getDb().doc(`goals/${projectId}/items/${goalId}`)
    const doc = await ref.get()
    if (doc.exists) {
        const data = doc.data()
        if (data.commentsData && data.commentsData.amount > 0) {
            ref.update({
                [`commentsData.lastComment`]: null,
                [`commentsData.lastCommentType`]: null,
                [`commentsData.amount`]: 0,
            })
        }
    }
}

export async function updateGoalProgress(projectId, progress, goal) {
    const { loggedUser } = store.getState()

    // If setting a Someday goal to 100%, move it to a done milestone
    if (progress === 100 && goal.completionMilestoneDate === BACKLOG_DATE_NUMERIC) {
        await moveCompletedGoalInBacklogToDone(projectId, goal)
    } else {
        updateGoalData(projectId, goal.id, { progress }, null)
        updateGoalProgressFeedsChain(projectId, progress, goal)
    }

    updateXpByChangeGoalProgress(loggedUser.uid, firebase, getDb(), projectId)
}

export async function setGoalDescription(projectId, goalId, description, goal, oldDescription) {
    updateGoalData(projectId, goal.id, { description }, null)

    createGenericTaskWhenMentionInTitleEdition(
        projectId,
        goalId,
        description,
        oldDescription,
        GENERIC_GOAL_TYPE,
        'goals',
        goal.assistantId
    )

    updateGoalDescriptionFeedsChain(projectId, goalId, description, goal, oldDescription)
}

export async function updateGoalProject(oldProject, newProject, goal) {
    const oldProjectId = oldProject.id
    const newProjectId = newProject.id

    const { projectUsers, loggedUser, route } = store.getState()
    const newProjectUsers = projectUsers[newProjectId]

    const assignees = newProjectUsers.filter(user => goal.assigneesIds.includes(user.uid))

    const assigneesIds = assignees.map(assignee => assignee.uid)
    if (assigneesIds.length === 0 || goal.assigneesIds.includes(DEFAULT_WORKSTREAM_ID))
        assigneesIds.push(DEFAULT_WORKSTREAM_ID)

    const assigneesCapacity = {}
    assigneesIds.forEach(id => {
        assigneesCapacity[id] = goal.assigneesCapacity[id] ? goal.assigneesCapacity[id] : CAPACITY_NONE
    })

    const assigneesReminderDate = await generateAssigneesReminderDate(
        goal.assigneesReminderDate,
        assigneesIds,
        newProject.id,
        goal.ownerId,
        goal.completionMilestoneDate
    )

    let isPublicFor
    if (goal.isPublicFor.includes(FEED_PUBLIC_FOR_ALL)) {
        isPublicFor = [FEED_PUBLIC_FOR_ALL]
    } else if (goal.isPublicFor.includes(DEFAULT_WORKSTREAM_ID)) {
        isPublicFor = [DEFAULT_WORKSTREAM_ID, ...newProjectUsers.map(user => user.uid)]
    } else {
        const usersWithAccess = newProjectUsers.filter(user => goal.isPublicFor.includes(user.uid))
        isPublicFor = usersWithAccess.map(user => user.uid)
    }

    const updatedGoal = {
        ...goal,
        focusAreaId: await resolveFocusAreaForProjectMove(oldProjectId, newProjectId, goal),
        assigneesIds,
        assigneesCapacity,
        assigneesReminderDate,
        parentDoneMilestoneIds: [],
        progressByDoneMilestone: {},
        isPublicFor,
        dateByDoneMilestone: {},
        sortIndexByMilestone: {},
        creatorId: newProjectUsers.map(user => user.uid).includes(goal.creatorId) ? goal.creatorId : loggedUser.uid,
    }

    await uploadNewGoal(newProjectId, updatedGoal, null, false, true)

    // Fetch all tasks associated with this goal and move them to the new project
    const taskDocs = await getDb().collection(`items/${oldProjectId}/tasks`).where('parentGoalId', '==', goal.id).get()

    const taskMovePromises = []
    taskDocs.forEach(doc => {
        const task = mapTaskData(doc.id, doc.data())
        taskMovePromises.push(setTaskProjectWithGoal(oldProject, newProject, task, updatedGoal))
    })
    await Promise.all(taskMovePromises)

    deleteGoal(oldProjectId, goal, newProjectId)

    if (route === 'GoalDetailedView') {
        NavigationService.navigate('GoalDetailedView', {
            goalId: goal.id,
            projectId: newProject.id,
        })

        const projectType = ProjectHelper.getTypeOfProject(loggedUser, newProject.id)
        store.dispatch([
            setSelectedSidebarTab(DV_TAB_ROOT_GOALS),
            switchProject(newProject.index),
            setSelectedTypeOfProject(projectType),
            setSelectedNavItem(DV_TAB_GOAL_PROPERTIES),
        ])
    }

    updateGoalProjectFeedsChain(oldProject, newProject, goal, assigneesIds)
}

export async function moveCompletedGoalInBacklogToDone(projectId, goal) {
    if (goal.progress !== 100) {
        updateGoalData(projectId, goal.id, { progress: 100 }, null)
        updateGoalProgressFeedsChain(projectId, 100, goal)
    }
    const todayDate = moment().startOf('day').hour(12).minute(0).valueOf()
    let milestone = await getMilestoneUsingDate(projectId, todayDate, true, goal.ownerId, MILESTONE_TYPE_FIXED)

    if (!milestone) {
        milestone = getNewDefaultGoalMilestone()
        milestone.date = todayDate
        milestone.done = true
        milestone.ownerId = goal.ownerId

        const milestoneId = getId()
        getDb().collection(`goalsMilestones/${projectId}/milestonesItems`).doc(milestoneId).set(milestone)
        milestone.id = milestoneId
    }

    addGoalsToDoneMilestone(
        projectId,
        milestone.id,
        milestone.date,
        milestone.doneDate,
        [{ ...goal, progress: 100 }],
        null
    )
    updateGoalSortIndexes(projectId, goal.id, milestone.id)
}

async function handleMilestonesExistenceWhenAGoalDateRangeChanges(
    projectId,
    goal,
    newDate,
    previousCompletionMilestoneDate
) {
    const promises = []
    if (newDate !== BACKLOG_DATE_NUMERIC) {
        if (goal.scheduleMode === GOAL_SCHEDULE_MODE_DYNAMIC) {
            const project = ProjectHelper.getProjectById(projectId)
            promises.push(
                ensureLinearMilestonesIncludingDate(projectId, goal.ownerId, project?.goalMilestonesConfig, newDate)
            )
        } else {
            promises.push(uploadOpenNewMilestoneIfNotExistMilestoneInSameDate(projectId, newDate, goal.ownerId))
        }
    }
    promises.push(deleteOpenMilestoneIfIsEmpty(projectId, previousCompletionMilestoneDate, [goal.id], goal.ownerId))
    await Promise.all(promises)
}

async function updateGoalsWhenChangeMilestoneDate(projectId, oldDate, newDate, ownerId, scheduleMode = null) {
    const goalsDocs = await getDb()
        .collection(`goals/${projectId}/items`)
        .where('completionMilestoneDate', '==', oldDate)
        .where('ownerId', '==', ownerId)
        .get()

    const batch = new BatchWrapper(getDb())

    goalsDocs.docs.forEach(doc => {
        const goal = mapGoalData(doc.id, doc.data())
        if (scheduleMode && goal.scheduleMode !== scheduleMode) return

        const toBacklog = newDate === BACKLOG_DATE_NUMERIC
        const moveFullGoalWhenUpdateCompletionDate =
            goal.startingMilestoneDate === goal.completionMilestoneDate || newDate < goal.startingMilestoneDate

        const moveFullGoal = toBacklog || moveFullGoalWhenUpdateCompletionDate

        const updateData = moveFullGoal
            ? {
                  startingMilestoneDate: newDate,
                  completionMilestoneDate: newDate,
                  assigneesReminderDate: updateAssigneesReminderDate(goal.assigneesIds, newDate),
              }
            : {
                  completionMilestoneDate: newDate,
                  assigneesReminderDate: updateAssigneesReminderDate(goal.assigneesIds, newDate),
              }
        updateGoalData(projectId, doc.id, updateData, batch)
    })
    await batch.commit()
}

function addGoalsToDoneMilestone(projectId, milestoneId, milestoneDate, doneDate, goals, externalBatch) {
    const batch = externalBatch ? externalBatch : new BatchWrapper(getDb())
    goals.forEach(goal => {
        updateGoalData(
            projectId,
            goal.id,
            {
                parentDoneMilestoneIds: firebase.firestore.FieldValue.arrayUnion(milestoneId),
                [`progressByDoneMilestone.${milestoneId}`]: {
                    progress: goal.progress === DYNAMIC_PERCENT ? goal.dynamicProgress : goal.progress,
                    doneDate,
                },
                [`dateByDoneMilestone.${milestoneId}`]: milestoneDate,
            },
            batch
        )
    })
    if (!externalBatch) batch.commit()
}

function removeGoalsFromDoneMilestone(projectId, milestoneId, goals, externalBatch) {
    const batch = externalBatch ? externalBatch : new BatchWrapper(getDb())
    goals.forEach(goal => {
        updateGoalData(
            projectId,
            goal.id,
            {
                parentDoneMilestoneIds: firebase.firestore.FieldValue.arrayRemove(milestoneId),
                [`progressByDoneMilestone.${milestoneId}`]: firebase.firestore.FieldValue.delete(),
                [`dateByDoneMilestone.${milestoneId}`]: firebase.firestore.FieldValue.delete(),
            },
            batch
        )
    })
    if (!externalBatch) batch.commit()
}

export async function updateFutureOpenMilestonesDateToBacklog(projectId, milestone) {
    const inDoneMilestone = milestone.done
    const milestoneDocs = (
        await getDb()
            .collection(`goalsMilestones/${projectId}/milestonesItems`)
            .where('date', '>', milestone.date)
            .where('done', '==', inDoneMilestone)
            .where('ownerId', '==', milestone.ownerId)
            .orderBy('date', 'desc')
            .get()
    ).docs

    const milestones = []
    milestoneDocs.forEach(doc => {
        const futureMilestone = mapMilestoneData(doc.id, doc.data())
        milestones.push(futureMilestone)
    })

    milestones.push(milestone)

    //NOT USE PROMISES HERE, BECAUSE WE NEED TO PROCESS THIS ONE BY ONE, BECAUSE THE RESULT OF THE FIRST AFFECT THE NEXTS
    for (let i = 0; i < milestones.length; i++) {
        await updateMilestoneDateToBacklog(projectId, milestones[i])
    }
}

export async function updateFutureOpenMilestonesDate(projectId, milestone, newDate) {
    const inDoneMilestone = milestone.done
    const moveMilestoneToTheFuture = milestone.date < newDate
    let timeDifference = ONE_DAY_MILLISECONDS * getPositiveDaysDifference(newDate, milestone.date)
    timeDifference = moveMilestoneToTheFuture ? timeDifference : timeDifference * -1

    const milestoneDocs = (
        await getDb()
            .collection(`goalsMilestones/${projectId}/milestonesItems`)
            .where('date', '>', milestone.date)
            .where('done', '==', inDoneMilestone)
            .where('ownerId', '==', milestone.ownerId)
            .orderBy('date', 'desc')
            .get()
    ).docs

    const milestones = []
    milestoneDocs.forEach(doc => {
        const futureMilestone = mapMilestoneData(doc.id, doc.data())
        milestones.push(futureMilestone)
    })

    milestones.push(milestone)

    if (!moveMilestoneToTheFuture) milestones.reverse()

    //NOT USE PROMISES HERE, BECAUSE WE NEED TO PROCESS THIS ONE BY ONE, BECAUSE THE RESULT OF THE FIRST AFFECT THE NEXTS
    for (let i = 0; i < milestones.length; i++) {
        const futureMilestone = milestones[i]
        const futureMilestoneNewDate = futureMilestone.date + timeDifference
        await updateMilestoneDate(projectId, futureMilestone, futureMilestoneNewDate)
    }
}

export async function updateMilestoneDateToBacklog(projectId, milestone) {
    const inDoneMilestone = milestone.done

    if (inDoneMilestone) {
        const goals = await getGoalsInDoneMilestone(projectId, milestone.id, [])
        const goalsToMove = goals.filter(goal => goal.progress !== DYNAMIC_PERCENT || goal.dynamicProgress !== 100)

        if (goals.length === goalsToMove.length) deleteMilestone(projectId, milestone.id)
        const backlogId = `${BACKLOG_MILESTONE_ID}${projectId}`
        goalsToMove.forEach(goal => {
            const batch = new BatchWrapper(getDb())
            const updateData = {
                completionMilestoneDate: BACKLOG_DATE_NUMERIC,
                startingMilestoneDate: BACKLOG_DATE_NUMERIC,
                parentDoneMilestoneIds: firebase.firestore.FieldValue.arrayRemove(milestone.id),
                [`progressByDoneMilestone.${milestone.id}`]: firebase.firestore.FieldValue.delete(),
                [`dateByDoneMilestone.${milestone.id}`]: firebase.firestore.FieldValue.delete(),
                progress: goal.progress === 100 ? 80 : goal.progress,
                assigneesReminderDate: updateAssigneesReminderDate(goal.assigneesIds, BACKLOG_DATE_NUMERIC),
            }
            updateGoalData(projectId, goal.id, updateData, batch)
            updateGoalSortIndexes(projectId, goal.id, backlogId)
            batch.commit()
        })
    } else {
        await updateGoalsWhenChangeMilestoneDate(
            projectId,
            milestone.date,
            BACKLOG_DATE_NUMERIC,
            milestone.ownerId,
            normalizeMilestoneType(milestone.milestoneType) === MILESTONE_TYPE_LINEAR
                ? GOAL_SCHEDULE_MODE_DYNAMIC
                : GOAL_SCHEDULE_MODE_FIXED
        )
        await deleteMilestone(projectId, milestone.id)
        await addOpenMilestoneSortIndexToGoalsInRange(
            projectId,
            milestone.date,
            BACKLOG_DATE_NUMERIC,
            milestone.ownerId
        )
    }
}

export async function updateMilestoneDate(projectId, milestone, newDate) {
    const inDoneMilestone = milestone.done
    if (inDoneMilestone) {
        const promises = []
        promises.push(getGoalsInDoneMilestone(projectId, milestone.id, []))
        promises.push(
            getMilestoneUsingDate(
                projectId,
                newDate,
                inDoneMilestone,
                milestone.ownerId,
                normalizeMilestoneType(milestone.milestoneType)
            )
        )
        const promiseResults = await Promise.all(promises)
        const goals = promiseResults[0]
        const milestoneInSameDate = promiseResults[1]

        if (milestoneInSameDate) {
            await deleteMilestone(projectId, milestone.id)
            const batch = new BatchWrapper(getDb())
            removeGoalsFromDoneMilestone(projectId, milestone.id, goals, batch)
            addGoalsToDoneMilestone(
                projectId,
                milestoneInSameDate.id,
                milestoneInSameDate.date,
                milestoneInSameDate.doneDate,
                goals,
                batch
            )
            batch.commit()
            addOpenMilestoneSortIndexToGoals(projectId, milestoneInSameDate.id, null, goals, milestone.ownerId)
        } else {
            await updateMilestone(projectId, { ...milestone, date: newDate })
            const batch = new BatchWrapper(getDb())
            goals.forEach(goal => {
                updateGoalData(projectId, goal.id, { [`dateByDoneMilestone.${milestone.id}`]: newDate }, batch)
            })
            batch.commit()
        }
    } else {
        await updateGoalsWhenChangeMilestoneDate(
            projectId,
            milestone.date,
            newDate,
            milestone.ownerId,
            normalizeMilestoneType(milestone.milestoneType) === MILESTONE_TYPE_LINEAR
                ? GOAL_SCHEDULE_MODE_DYNAMIC
                : GOAL_SCHEDULE_MODE_FIXED
        )
        await updateOrDeleteMilestoneWhenChangesDate(projectId, milestone, newDate, inDoneMilestone)

        const date1 = milestone.date < newDate ? milestone.date : newDate
        const date2 = milestone.date > newDate ? milestone.date : newDate
        await addOpenMilestoneSortIndexToGoalsInRange(projectId, date1, date2, milestone.ownerId)
    }
}

async function updateOrDeleteMilestoneWhenChangesDate(projectId, milestone, newDate, searchInDoneMilestones) {
    const milestoneInSameDate = await getMilestoneUsingDate(
        projectId,
        newDate,
        searchInDoneMilestones,
        milestone.ownerId,
        normalizeMilestoneType(milestone.milestoneType)
    )
    milestoneInSameDate
        ? await deleteMilestone(projectId, milestone.id)
        : await updateMilestone(projectId, { ...milestone, date: newDate })
}

async function uploadOpenNewMilestoneIfNotExistMilestoneInSameDate(projectId, date, ownerId) {
    if (date !== BACKLOG_DATE_NUMERIC) {
        const milestoneInSameDate = await getMilestoneUsingDate(projectId, date, false, ownerId, MILESTONE_TYPE_FIXED)
        if (!milestoneInSameDate) {
            const milestone = getNewDefaultGoalMilestone()
            milestone.date = date
            milestone.ownerId = ownerId
            milestone.milestoneType = MILESTONE_TYPE_FIXED
            const milestoneId = getId()
            const promises = []
            promises.push(
                getDb().collection(`goalsMilestones/${projectId}/milestonesItems`).doc(milestoneId).set(milestone)
            )
            promises.push(
                addOpenMilestoneSortIndexToGoals(
                    projectId,
                    milestoneId,
                    milestone.date,
                    null,
                    ownerId,
                    GOAL_SCHEDULE_MODE_FIXED
                )
            )
            await Promise.all(promises)
        }
    }
}

async function deleteOpenMilestoneIfIsEmpty(projectId, milestoneDate, idsOfGoalsToExclude, ownerId) {
    if (milestoneDate === BACKLOG_DATE_NUMERIC) return
    const isEmpty =
        (
            await getBaseGoalsInOpenMilestone(
                projectId,
                milestoneDate,
                idsOfGoalsToExclude,
                ownerId,
                GOAL_SCHEDULE_MODE_FIXED
            )
        ).length === 0
    if (isEmpty) {
        const milestone = await getMilestoneUsingDate(projectId, milestoneDate, false, ownerId, MILESTONE_TYPE_FIXED)
        if (milestone) await deleteMilestone(projectId, milestone.id)
    }
}

export async function updateMilestone(projectId, updatedMilestone) {
    const milestoneId = updatedMilestone.id
    delete updatedMilestone.id
    await getDb().doc(`goalsMilestones/${projectId}/milestonesItems/${milestoneId}`).update(updatedMilestone)
}

async function deleteMilestone(projectId, milestoneId) {
    await getDb().doc(`goalsMilestones/${projectId}/milestonesItems/${milestoneId}`).delete()
}

export async function updateGoalMilestoneAssigneesCapacity(projectId, milestoneId, newCapacity, assigneeId) {
    getDb()
        .doc(`goalsMilestones/${projectId}/milestonesItems/${milestoneId}`)
        .update({
            [`assigneesCapacityDates.${assigneeId}`]: newCapacity,
        })
}

export async function updateMilestoneDoneState(projectId, milestone) {
    return runHttpsCallableFunction('updateMilestoneDoneStateSecondGen', {
        projectId,
        milestoneId: milestone.id,
        targetDone: !milestone.done,
    })
}

const updatePrivacyInChildrenTasks = (projectId, goalId, isPublicFor) => {
    getDb()
        .collection(`/items/${projectId}/tasks`)
        .where('parentGoalId', '==', goalId)
        .get()
        .then(docs => {
            docs.forEach(doc => {
                getDb().doc(`items/${projectId}/tasks/${doc.id}`).update({ parentGoalIsPublicFor: isPublicFor })
            })
        })
}

//OTHERS FUNCTIONS

const updateAssigneesReminderDate = (assigneesIds, date) => {
    const assigneesReminderDate = {}
    assigneesIds.forEach(assigneeId => {
        assigneesReminderDate[assigneeId] = date
    })
    return assigneesReminderDate
}

const generateAssigneesReminderDate = async (
    assigneesReminderDate,
    newAssigneesIds,
    projectId,
    ownerId,
    completionMilestoneDate
) => {
    const previousMilestone = await getPreviousMilestone(projectId, ownerId, completionMilestoneDate)
    const newAssigneesReminderDate = {}
    newAssigneesIds.forEach(assigneeId => {
        newAssigneesReminderDate[assigneeId] = assigneesReminderDate[assigneeId]
            ? assigneesReminderDate[assigneeId]
            : previousMilestone
              ? moment(previousMilestone.date).add(1, 'days').valueOf()
              : Date.now()
    })

    return newAssigneesReminderDate
}

function getGoalDirectionWhenChangesDate(newDate, goal, rangeEdgePropertyName) {
    const toBacklog = newDate === BACKLOG_DATE_NUMERIC
    const fromBacklog = goal.completionMilestoneDate === BACKLOG_DATE_NUMERIC
    const moveFullGoalWhenUpdateCompletionDate =
        rangeEdgePropertyName === 'completionMilestoneDate' &&
        (goal.startingMilestoneDate === goal.completionMilestoneDate || newDate < goal.startingMilestoneDate)
    const moveFullGoalWhenUpdateStartingDate =
        rangeEdgePropertyName === 'startingMilestoneDate' && newDate > goal.completionMilestoneDate
    return { toBacklog, fromBacklog, moveFullGoalWhenUpdateCompletionDate, moveFullGoalWhenUpdateStartingDate }
}

const getNewIsPublicForWhenAssigneesChanges = (projectId, isPublicFor, newAssigneesIds) => {
    if (isPublicFor.includes(FEED_PUBLIC_FOR_ALL)) {
        return [FEED_PUBLIC_FOR_ALL]
    } else {
        const userWithPermanentAccessIds = getAllOwnerIds(projectId, newAssigneesIds)
        return [...isPublicFor, ...userWithPermanentAccessIds.filter(id => !isPublicFor.includes(id))]
    }
}

export function sortGoalsAlphabetically(projectId, milestoneId, goals) {
    const sortedGoals = sortBy(goals, [goal => goal.extendedName])

    const batch = getDb().batch()
    sortedGoals.forEach((goal, index) => {
        batch.update(getDb().doc(`goals/${projectId}/items/${goal.id}`), {
            [`sortIndexByMilestone.${milestoneId}`]: sortedGoals.length - index,
        })
    })
    batch.commit()
}

//SORT INDEX FUNCTIONS

export function updateGoalSortIndexWithBatch(projectId, goalId, milestoneId, batch) {
    const sortIndex = generateSortIndex()
    batch.set(
        getDb().doc(`goals/${projectId}/items/${goalId}`),
        {
            sortIndexByMilestone: { [milestoneId]: sortIndex },
        },
        { merge: true }
    )
    return sortIndex
}

async function updateGoalSortIndexes(projectId, goalId, milestoneId) {
    const sortIndexData = { [milestoneId]: generateSortIndex() }
    await getDb()
        .doc(`goals/${projectId}/items/${goalId}`)
        .set({ sortIndexByMilestone: sortIndexData }, { merge: true })
}

async function updateAllOpenGoalSortIndexs(projectId, goal, useNegativeSortIndex) {
    const { completionMilestoneDate } = goal

    let newSortIndexByMilestone = {}

    const backlogId = `${BACKLOG_MILESTONE_ID}${projectId}`
    const milestones =
        completionMilestoneDate === BACKLOG_DATE_NUMERIC
            ? [{ id: backlogId }]
            : await getOpenMilestonesFromGoal(projectId, goal)

    milestones.forEach(milestone => {
        fillEmptyMilestoneGoalSortIndex(milestone.id, goal, newSortIndexByMilestone, useNegativeSortIndex)
    })

    const finalMilestonesIds = Object.keys(newSortIndexByMilestone)
    if (finalMilestonesIds.length > 0) {
        await getDb()
            .doc(`goals/${projectId}/items/${goal.id}`)
            .set({ sortIndexByMilestone: newSortIndexByMilestone }, { merge: true })
    }
}

async function fillEmptyMilestoneGoalSortIndex(milestoneId, goal, newSortIndexByMilestone, useNegativeSortIndex) {
    const { sortIndexByMilestone } = goal
    if (sortIndexByMilestone[milestoneId]) {
        newSortIndexByMilestone[milestoneId] = sortIndexByMilestone[milestoneId]
    } else {
        newSortIndexByMilestone[milestoneId] = useNegativeSortIndex ? generateNegativeSortIndex() : generateSortIndex()
    }
}

async function addOpenMilestoneSortIndexToGoals(
    projectId,
    milestoneId,
    milestoneDate,
    milestoneGoals,
    ownerId,
    scheduleMode = null
) {
    const goals = milestoneGoals
        ? milestoneGoals
        : await getGoalsInOpenMilestone(projectId, milestoneDate, [], true, ownerId, scheduleMode)
    const promises = []
    goals.forEach(goal => {
        promises.push(updateGoalSortIndexes(projectId, goal.id, milestoneId))
    })
    await Promise.all(promises)
}

async function addOpenMilestoneSortIndexToGoalsInRange(projectId, date1, date2, ownerId) {
    const milestones = await getOpenMilestonesInDateRange(projectId, date1, date2, ownerId)
    const promises = []
    milestones.forEach(milestone => {
        promises.push(
            addOpenMilestoneSortIndexToGoals(
                projectId,
                milestone.id,
                milestone.date,
                null,
                ownerId,
                normalizeMilestoneType(milestone.milestoneType) === MILESTONE_TYPE_LINEAR
                    ? GOAL_SCHEDULE_MODE_DYNAMIC
                    : GOAL_SCHEDULE_MODE_FIXED
            )
        )
    })
    if (date2 === BACKLOG_DATE_NUMERIC) promises.push(addBacklogSortIndexToGoalsInBacklog(projectId, ownerId))
    await Promise.all(promises)
}

async function addBacklogSortIndexToGoalsInBacklog(projectId, ownerId) {
    const backlogId = `${BACKLOG_MILESTONE_ID}${projectId}`
    const goals = await getGoalsInOpenMilestone(projectId, BACKLOG_DATE_NUMERIC, [], true, ownerId)
    const promises = []
    goals.forEach(goal => {
        promises.push(updateGoalSortIndexes(projectId, goal.id, backlogId))
    })
    await Promise.all(promises)
}
